import { format, isAfter, isSameDay, isSameYear, isValid, parse } from "date-fns";
import { useCurrency } from "~/composables/currency/useCurrency";

const DATE_TEXT_FORMAT = "dd/MM/yyyy";

/** The leading `yyyy-mm-dd` of an API date. */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

function dateTimeParts(date: Date, timeZone?: string): Record<string, string> {
  const formatter = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  });

  return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
}

export const useFormats = () => {
  const { formatPrice: toCurrency } = useCurrency();

  function formatTo2Decimals(num: number): number {
    return num % 1 === 0 ? num : Number(num.toFixed(2));
  }

  function toLocalNumber(number: number) {
    return new Intl.NumberFormat("fr-FR").format(number);
  }

  function toStringDisplay(...val: string[]) {
    val = val.filter(Boolean);
    return val.length ? val.join(" ") : "";
  }

  // Capitalizes each segment rather than only the first letter: uppercasing the head and lowercasing
  // the rest turned "Pierre-Mickael" into "Pierre-mickael", while dropping the transform entirely
  // would surface the ~25 % of first names stored in ALL CAPS as "JEAN".
  function toNameDisplay(name: string): string {
    return name
      .toLocaleLowerCase("fr")
      .replace(/(^|[\s\-'’])(\p{L})/gu, (_match, separator: string, letter: string) => {
        return separator + letter.toLocaleUpperCase("fr");
      });
  }

  /** "Agathe Manager" → "AM", for an avatar when only the display name is known. */
  function toInitials(name: string | null | undefined): string {
    return (name ?? "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  }

  function toPercentageDisplay(nb: number) {
    if (isNaN(nb) || !isFinite(nb)) return `${parseFloat(Number(0).toFixed(2))} %`;
    return `${parseFloat(Number(nb).toFixed(2))} %`;
  }

  // A null date used to throw here, and a throw inside a grid cell aborts the whole page patch —
  // the DOM then stays frozen half-rendered instead of just missing a value.
  function toDateDisplay(date: string | Date | null | undefined): string {
    if (!date) return "-";
    if (date instanceof Date) {
      const day = date.getUTCDate().toString().padStart(2, "0");
      const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = date.getUTCFullYear();
      return `${day}/${month}/${year}`;
    } else {
      return toDateDisplayLocal(date);
    }
  }

  function toDateDisplayLocal(dateString: string): string {
    // Handle formats: "2025-06-14", "2025-06-14T00:00:00", "2025-06-14 00:00:00"
    const parts = dateString.split(/[-T\s]/);
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const day = parseInt(parts[2]);

    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }

  function toDateDisplayNoUTC(date: string | Date): Date {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      date = new Date(year, month, day);
    } else {
      // Handle formats: "2025-06-14", "2025-06-14T00:00:00", "2025-06-14 00:00:00"
      const parts = (date as string).split(/[-T\s]/);
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      date = new Date(year, month, day);
    }
    return date;
  }

  // dd/MM/yyyy text of a local date, empty without a valid one
  function formatDateText(date: Date | null): string {
    return date && isValid(date) ? format(date, DATE_TEXT_FORMAT) : "";
  }

  // Local date of a complete dd/MM/yyyy text, null while partial or impossible
  function parseDateText(text: string): Date | null {
    if (text.length !== DATE_TEXT_FORMAT.length) return null;
    const date = parse(text, DATE_TEXT_FORMAT, new Date());
    return isValid(date) ? date : null;
  }

  // Whether a complete text names a day that does not exist (31/02…)
  function isImpossibleDateText(text: string): boolean {
    return text.length === DATE_TEXT_FORMAT.length && !parseDateText(text);
  }

  // Masks typed text as jj/mm/aaaa and keeps the caret after the same digit
  function maskDateText(value: string, caret: number) {
    const digitsBeforeCaret = value.slice(0, caret).replace(/\D/g, "").length;
    const digits = value.replace(/\D/g, "").slice(0, 8);
    const text = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join("/");
    // Slashes sit after the 2nd and 4th digits
    const maskedCaret = digitsBeforeCaret + (digitsBeforeCaret > 2 ? 1 : 0) + (digitsBeforeCaret > 4 ? 1 : 0);
    return { text, caret: Math.min(maskedCaret, text.length) };
  }

  // Both bounds in chronological order, null until both are set
  function orderedDateRange(start: Date | null, end: Date | null): [Date, Date] | null {
    if (!start || !end) return null;
    return isAfter(start, end) ? [end, start] : [start, end];
  }

  // Start and end of a date picker value, "Vide" excluded
  function dateRangeBounds(value: (Date | null)[]): [Date | null, Date | null] {
    const [start, end] = value.filter((date): date is Date => !!date && isValid(date));
    return [start ?? null, end ?? null];
  }

  // « 12/10 → 23/10/2026 »: the year is written once when both bounds share it
  function toDateRangeDisplay(start: Date, end: Date): string {
    if (isSameDay(start, end)) return formatDateText(start);
    return `${format(start, isSameYear(start, end) ? "dd/MM" : DATE_TEXT_FORMAT)} → ${formatDateText(end)}`;
  }

  // « 15/09 » of an API calendar date, the year left out; empty without a valid one
  function toDayMonthDisplay(value: string | null | undefined): string {
    const date = parseCalendarDate(value);
    return date ? format(date, "dd/MM") : "";
  }

  // « Novembre 2026 »
  function toMonthYearDisplay(date: Date): string {
    const text = date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // yyyy-MM-dd of the local day, where toDateISOString reads a Date in UTC
  function toLocalDateISOString(date: Date): string {
    return format(date, "yyyy-MM-dd");
  }

  function toDateWithTime(date: string | Date): string {
    const newDate = new Date(date);
    const startOfDay = new Date(date).setHours(0, 0, 0, 0);
    const startDate = new Date(startOfDay);

    if (newDate > startDate) {
      return dateFormat(newDate, true);
    } else {
      return dateFormat(newDate);
    }
  }

  function dateFormat(date: Date, hour = false) {
    if (hour) {
      return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date
        .toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "/");
    }
  }

  function toDateWithTimeNormalized(date: string | Date, dateOnly = false): string {
    // Normaliser le format de date si c'est une string
    let normalizedDate = date;
    if (typeof date === "string") {
      // Corriger les formats timezone non-standard comme +02Z, +1Z, +10Z, etc.
      // Les convertir en format ISO standard +HH:MM
      normalizedDate = date.replace(/\+(\d{1,2})Z$/, (match, hours) => {
        const paddedHours = hours.padStart(2, "0");
        return `+${paddedHours}:00`;
      });
    }

    const newDate = new Date(normalizedDate);

    // Vérifier si la date est valide
    if (isNaN(newDate.getTime())) {
      console.error("Date invalide:", date, "→ normalisée:", normalizedDate);
      return "";
    }

    if (dateOnly) {
      return dateFormat(newDate, false);
    }

    const startOfDay = new Date(normalizedDate).setHours(0, 0, 0, 0);
    const startDate = new Date(startOfDay);

    if (newDate > startDate) {
      return dateFormat(newDate, true);
    } else {
      return dateFormat(newDate);
    }
  }

  function toDateLongDisplay(date: string | Date): string {
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };

    if (date instanceof Date) {
      // Create a new date without UTC issues
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const localDate = new Date(year, month, day);
      return localDate.toLocaleDateString("fr-FR", options);
    } else {
      // Parse manually to avoid UTC conversion
      // Handle formats: "2025-06-14", "2025-06-14T00:00:00", "2025-06-14 00:00:00"
      const parts = date.split(/[-T\s]/);
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // Month is 0-indexed in Date constructor
      const day = parseInt(parts[2]);
      const localDate = new Date(year, month, day);
      return localDate.toLocaleDateString("fr-FR", options);
    }
  }

  function toDateISOString(date: Date | string): string {
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    } else {
      return date.split("T")[0];
    }
  }

  function toTimeDisplay(time: string | null | undefined): string {
    if (!time) return "";
    // Handle formats: "19:00:00", "19:00", "19:00:00.000"
    const parts = time.split(":");
    if (parts.length >= 2) {
      const hours = parts[0];
      const minutes = parts[1];
      return `${hours}h${minutes}`;
    }
    return time;
  }

  /*
   * A CALENDAR DAY — a connector's cutoff, say — has three representations that must not be
   * confused:
   *
   *  - on the wire, to the API: UTC midnight of the day — "2026-01-01T00:00:00.000Z";
   *  - back from the API: the same day in Postgres text form — "2026-01-01 00:00:00";
   *  - out of the date picker: the INSTANT of the click, as an ISO string — the clicked day at the
   *    current time of day, e.g. "2026-06-16T00:30:00.000Z" for a click on 15/06 at 20:30 in New York.
   *
   * The first two carry the day in their leading `yyyy-mm-dd`; the third does not, and reading it
   * that way saves the day before or after the one clicked. In a form the day is always a local Date
   * at noon, which is unambiguous and clear of daylight-saving shifts.
   */

  /**
   * The calendar day carried by an API date, as a local date at noon.
   *
   * `new Date(value)` would read it as an instant, and show the day before in any browser west of
   * UTC — so only the leading `yyyy-mm-dd` is read.
   */
  function parseCalendarDate(value: string | null | undefined): Date | null {
    const match = value ? CALENDAR_DAY.exec(value) : null;
    if (!match) return null;

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  }

  /**
   * The day the admin clicked in the date picker, as a local date at noon.
   *
   * `DateInput` hands the picker `utc="false"`, a truthy string, so what it emits is not a Date but
   * the picked instant as an ISO string. The clicked day is that instant's LOCAL day.
   */
  function pickedCalendarDay(value: Date | string | null | undefined): Date | null {
    if (!value) return null;

    const instant = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(instant.getTime())) return null;

    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate(), 12);
  }

  /**
   * A day of a form as UTC midnight, the form the connector endpoints expect.
   *
   * Built from the LOCAL year, month and day: `toISOString()` converts to UTC first, and sends the
   * day before from any browser east of UTC. A string is read as a picker instant, so that a picked
   * value reaching a payload unconverted still sends the day that was clicked.
   */
  function toUtcMidnightIso(value: Date | string | null | undefined): string | null {
    const date = pickedCalendarDay(value);
    if (!date) return null;

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${date.getFullYear()}-${month}-${day}T00:00:00.000Z`;
  }

  /**
   * An instant as `dd/MM/yyyy HH:mm` in the given IANA timezone, or `null` without a valid date. For
   * a schedule, the zone its cron hour is expressed in: "04:00" then reads the same whatever timezone
   * the admin's machine is in. An unknown zone falls back to the machine's rather than hiding the date.
   */
  function toDateTimeDisplayInZone(instant: string | Date | null | undefined, timeZone?: string): string | null {
    if (!instant) return null;

    const date = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(date.getTime())) return null;

    let parts: Record<string, string>;
    try {
      parts = dateTimeParts(date, timeZone);
    } catch {
      parts = dateTimeParts(date);
    }

    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
  }

  /** An instant as `dd/MM/yyyy à HH:mm` in the viewer's timezone, or `null` without a valid date. */
  function toDateAtTimeDisplay(instant: string | Date | null | undefined): string | null {
    if (!instant) return null;

    const date = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(date.getTime())) return null;

    const parts = dateTimeParts(date);
    return `${parts.day}/${parts.month}/${parts.year} à ${parts.hour}:${parts.minute}`;
  }

  return {
    formatTo2Decimals,
    toLocalNumber,
    toCurrency,
    toStringDisplay,
    toNameDisplay,
    toInitials,
    toDateDisplay,
    toPercentageDisplay,
    toDateLongDisplay,
    toDateWithTime,
    toDateISOString,
    toDateDisplayLocal,
    toDateDisplayNoUTC,
    toDateWithTimeNormalized,
    toTimeDisplay,
    formatDateText,
    parseDateText,
    isImpossibleDateText,
    maskDateText,
    orderedDateRange,
    dateRangeBounds,
    parseCalendarDate,
    pickedCalendarDay,
    toUtcMidnightIso,
    toDateTimeDisplayInZone,
    toDateAtTimeDisplay,
    toDateRangeDisplay,
    toDayMonthDisplay,
    toMonthYearDisplay,
    toLocalDateISOString,
  };
};
